import { createBrowserClient } from '@supabase/ssr';
import type { SupabaseClient } from '@supabase/supabase-js';

const PFX = 'sb_';

const canUseCookies = (() => {
  let cache: boolean | null = null;
  return () => {
    if (typeof document === 'undefined') return false;
    if (cache !== null) return cache;
    const k = '__sb_test__';
    document.cookie = `${k}=1; Path=/; SameSite=None; Secure; Partitioned`;
    cache = document.cookie.includes(k);
    document.cookie = `${k}=; Path=/; Max-Age=0; SameSite=None; Secure`;
    return cache;
  };
})();

const fromCookies = () =>
  typeof document === 'undefined'
    ? []
    : document.cookie
        .split(';')
        .filter(Boolean)
        .map((c) => {
          const [name, ...rest] = c.trim().split('=');
          return { name: name.trim(), value: decodeURIComponent(rest.join('=')) };
        })
        .filter((c) => c.name);

const fromStorage = () => {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX))
      .map((k) => ({ name: k.slice(PFX.length), value: localStorage.getItem(k) || '' }));
  } catch {
    return [];
  }
};

const setCookie = (name: string, value: string, options?: any) => {
  let s = `${name}=${encodeURIComponent(value)}; Path=${options?.path || '/'}; SameSite=None; Secure; Partitioned`;
  if (options?.maxAge) s += `; Max-Age=${options.maxAge}`;
  if (options?.domain) s += `; Domain=${options.domain}`;
  if (options?.expires) s += `; Expires=${new Date(options.expires).toUTCString()}`;
  document.cookie = s;
};

// Custom lock implementation that prevents "lock stolen" errors.
// Instead of competing for a Web Lock, we use a simple in-memory promise queue
// so concurrent auth operations are serialized without throwing.
let _lockHolder: Promise<unknown> = Promise.resolve();

function acquireLock<T>(name: string, acquireTimeout: number, fn: () => Promise<T>): Promise<T> {
  const result = _lockHolder.then(() => fn()).catch((err) => {
    // If the first attempt fails due to a network error during token refresh,
    // return a rejected promise so Supabase can handle it gracefully
    return Promise.reject(err);
  });
  // Chain onto the holder so the next caller waits for this one
  _lockHolder = result.catch(() => {});
  return result as Promise<T>;
}

// Singleton instance to prevent multiple clients competing for the auth lock
let clientInstance: SupabaseClient | null = null;

export function createClient() {
  if (typeof window === 'undefined') {
    // Server-side: always create a new instance (no singleton needed)
    return createBrowserClient(
      process.env.NEXT_PUBLIC_SUPABASE_URL!,
      process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
      {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
        },
        cookies: {
          getAll: () => [],
          setAll: () => {},
        },
      }
    );
  }

  // Client-side: reuse singleton to avoid lock contention
  if (clientInstance) return clientInstance;

  clientInstance = createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        // Disable detectSessionInUrl to prevent race conditions on initial page load
        detectSessionInUrl: false,
        // Custom lock prevents "lock was released because another request stole it"
        // by serializing auth operations in-memory instead of using Web Locks API
        lock: acquireLock,
      },
      cookies: {
        getAll: () => (canUseCookies() ? fromCookies() : fromStorage()),
        setAll(cookiesToSet) {
          if (typeof document === 'undefined') return;
          if (canUseCookies()) {
            cookiesToSet.forEach(({ name, value, options }) =>
              value
                ? setCookie(name, value, options)
                : (document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`)
            );
          } else {
            cookiesToSet.forEach(({ name, value, options }) => {
              try {
                value
                  ? localStorage.setItem(`${PFX}${name}`, value)
                  : localStorage.removeItem(`${PFX}${name}`);
              } catch {}
              if (value) setCookie(name, value, options);
            });
          }
        },
      },
    }
  );

  return clientInstance;
}
